/* React Components */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { supabase } from '../../services/Supabase';
import { streamOpenAIResponse } from '../../services/openaiResponse';
/* Providers */
import { useAuth } from '../../context/AuthContext';
import commonFunction from '../../utils/CommonFunction';
/* Components */
import SideBar from "../../components/SideBar";
import ChatBox from '../../components/ChatBox';
import UpgradeRequired from '../../components/UpgradeRequired';
/* Styles and Images */
import CompanyLogoWhite from "../../assets/img/m-comp-logo-white.png";
import CompanyLogoDark from "../../assets/img/m-comp-logo-black.png";
import spinner from "../../assets/img/loader-new.svg";
/* React Icons */
import { BsChatDots } from "react-icons/bs";
import { RiUser3Line } from "react-icons/ri";


const UserCategoryThreads = () => {
    /* Providers and Headers */
    const { convertN2lbr, convertTextPattern, getDataArray, formatRelativeDate, mainSystemInstructions } = commonFunction();
    const navigate = useNavigate();
    const { categorySlug } = useParams(); // get :categorySlug from URL
    const [searchParams] = useSearchParams(); // get :question from URL
    const messagesEndRef = useRef(null);
    const hasRunRef = useRef(false);

    /* User Information */
    const { user, setUserTheme, userTheme, userMetaData, hasARIAccess } = useAuth();
    const userId = user?.id ?? '';
    const user_nickname = userMetaData?.nickname ? userMetaData?.nickname : (user?.first_name ?? 'You');

    /* Use States / Setters */
    const companyLogo  = (userTheme === 'dark-mode') ? CompanyLogoWhite : CompanyLogoDark;
    const [inputMessage, setInputMessage] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState(true);
    const [threadID, setThreadID] = useState('');
    const [systemInstruction, setSystemInstruction] = useState('');
    const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [categoryDetails, setCategoryDetails] = useState([]);
    const [categoryID, setCategoryID] = useState('');
    const question = searchParams.get("question");

    // For Input Focus
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [inputHeight, setInputHeight] = useState('auto');
    const formRef = useRef(null);
    const chatContainerRef = useRef(null);
    const textareaRef = useRef(null);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            if (event.shiftKey) {
                return;
            }
            event.preventDefault();
            onSubmit(event);
        }
    };
    
    function onChange(e){
        setInputHeight(`${e.target.scrollHeight}px`);
        setInputMessage(e.target.value);
        if(e.target.value===''){
            setInputHeight('auto');
        }
    }

    useEffect(() => {
        textareaRef.current?.focus();
    }, [isBotTyping, categorySlug]);

    useEffect(() => {
        let isActive = true;

        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }

        setLoadingMsg(true);
        setChatMessages([]);

        const getDailyRoutineMsg = async (resolvedCategoryId) => {
            try {
                if (!resolvedCategoryId) {
                    setLoadingMsg(false);
                    return null;
                }

                const { data, error } = await supabase
                    .from('user_chat_history')
                    .select('*')
                    .eq('user_id', userId)
                    .eq('category_id', resolvedCategoryId)
                    .order('id', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                if (!isActive) return null;

                if (error) {
                    console.error('Error fetching latest chat history:', error);
                    setLoadingMsg(false);
                    return null;
                }

                setThreadID(data?.thread_id ?? '');

                const messagesArray = getDataArray(data?.messages);
                if (messagesArray.length > 0) {
                    setChatMessages(messagesArray);
                }

                setLoadingMsg(false);
                return data;
            } catch (err) {
                if (isActive) {
                    console.error(err);
                    setLoadingMsg(false);
                }
                return null;
            }
        };

        const getSystemInstruction = async () => {
            const instructionText = await mainSystemInstructions();
            if (isActive) {
                setSystemInstruction(instructionText);
            }
        };

        const fetchCategoryDetails = async () => {
            if (!categorySlug) {
                navigate("/dashboard", { replace: true });
                return;
            }

            try {
                const { data, error } = await supabase
                    .from("coaching_categories")
                    .select("*")
                    .eq("slug", categorySlug)
                    .maybeSingle();

                if (!isActive) return;

                if (error || !data) {
                    navigate("/dashboard", { replace: true });
                    return;
                }

                setCategoryDetails(data);
                setCategoryID(data.id);

                await getDailyRoutineMsg(data.id);
            } catch (err) {
                if (isActive) {
                    console.error(err);
                    setLoadingMsg(false);
                }
            }
        };

        getSystemInstruction();
        fetchCategoryDetails();

        return () => {
            isActive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categorySlug, userId, navigate, question]);

    useEffect(() => {
        if (hasRunRef.current) return;

        if (
            question &&
            question.trim() !== "" &&
            categorySlug &&
            userId &&
            hasARIAccess &&
            categoryID // ✅ THIS is what you actually need
        ) {
            hasRunRef.current = true;

            const run = async () => {
                // The actual reflection question should be the USER message
                setInputMessage(`Guide me through this reflection: ${question}. Ask me to answer first. After I respond, provide feedback and ask one follow-up question. `);

                if(chatMessages.length===0 || categoryID){
                    setTimeout(() => {
                        formRef.current?.requestSubmit();
                    }, 1000);
                    navigate(`/category/${categorySlug}`, { replace: true });
                }
            };

            run();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question, categorySlug, userId, categoryID, chatMessages, hasARIAccess]);

    // -------------------- SCROLL --------------------
    useEffect(() => {
        scrollToBottom();
    }, [chatMessages, currentStreamingMessage]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // -------------------- SAVE MESSAGES --------------------
    const onSubmit = async (e) => {
        e.preventDefault();
    
        // ✅ allow programmatic submit override
        const messageToSend = e?.injectedMessage || inputMessage;

        if (!messageToSend || !messageToSend.trim()) {
            toast.warning("Please enter your questions in the text area.");
            return;
        }

        if (isThinking) return; // prevent double submit

        setIsThinking(true);
        setIsBotTyping(true);
        setCurrentStreamingMessage(""); // clear previous stream
        setInputMessage(messageToSend.trim());
        
        const userMessage = {
            type: "user",
            name: user_nickname,
            text: messageToSend.trim(),
            timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...chatMessages, userMessage];
        setChatMessages(updatedMessages);
        setInputMessage("");

        // reuse same streaming logic pattern
        let fullResponse = "";

        try {
            // ✅ Build clean system instruction
            const finalSystemInstruction = `
                ${systemInstruction || ""}
                Category Name: ${categoryDetails?.category_name ?? 'N/A'}
                Category Context:
                ${categoryDetails?.category_description || ""}
            `.trim();

            const apiInput = [
                { role: "system", content: finalSystemInstruction },
                ...updatedMessages.map((msg) => ({
                    role: msg.type === "user" ? "user" : "assistant",
                    content: msg.text,
                })),
            ];

            fullResponse = await streamOpenAIResponse({
                model: "gpt-4o-mini",
                input: apiInput,
                temperature: 0.7,
                onTextDelta: (_delta, streamedText) => {
                    setCurrentStreamingMessage(streamedText);
                },
            });

            const aiMessage = {
                type: "ai",
                name: "ARI",
                text: fullResponse,
                timestamp: new Date().toISOString(),
            };

            const finalMessages = [...updatedMessages, aiMessage];
            await saveMessagesToStorage(finalMessages, messageToSend.trim());
            setChatMessages(finalMessages);
            setCurrentStreamingMessage("");
        } catch (err) {
            console.error(err);
        } finally {
            setIsThinking(false);
            setInputHeight('auto');
            setIsBotTyping(false);
        }
    };
    
    const saveMessagesToStorage = async (messages, firstQuestion='') => {
        var theThreadID = '';
        if(threadID){
            const { error } = await supabase
                .from("user_chat_history")
                .update({ messages })
                .eq("thread_id", threadID)
                .eq("user_id", userId);

            if (error) {
                console.error(error.message);
                return false;
            }
            theThreadID = threadID;
        }else{
            const { data, error } = await supabase
            .from("user_chat_history")
            .insert({
                user_id: userId,
                category_id: categoryID,
                first_question: firstQuestion,
                messages: messages,
            }).select().maybeSingle();

            if (error) {
                toast.error("Problem creating conversation. " + error.message);
                return false;
            }
            
            theThreadID = data.thread_id;

            // clear textarea before redirect
            setThreadID(data.thread_id);
        }
        return theThreadID;
    };

    function maskReflectionPrompt(text) {
        const REFLECTION_PATTERN = /guide me through this reflection[:\s]+(.+?)[.!]\s*ask me to answer first.*$/i;
        const match = text.match(REFLECTION_PATTERN);
        if (!match) return { masked: false, text };
        return { masked: true, question: match[1].trim() };
    }

    return (
        <>
            <section className={`chat-body-container ${userTheme}`}>
                {/* Sidebar */}
                <SideBar userTheme={userTheme} setUserTheme={setUserTheme}/> 
                
                {/* Main Chat Interface */}
                {(hasARIAccess) ? (
                    <div className="main-chat-container">
                        <div className="chat-box-top-container">
                            <a href="/" className='chat-heading-container'>
                                <span>{categoryDetails.category_name ?? 'Welcome to ARI'}</span>
                            </a>
                        </div>
                        
                        <div className="chat-box-center-container" ref={chatContainerRef}>

                            {(loadingMsg === true) ? 
                                (
                                    <div className="load-chat-spinner-loader">
                                        <img className='loaderCompanyLogo' src={companyLogo} alt="Loading..." />
                                        <img className='loaderSpinner' src={spinner} alt="Loading..." />
                                        <p>Fetching threads...</p>
                                    </div>
                                ) :
                                (
                                    (isThinking || threadID) ? (
                                        <>
                                            <div className="chat-ui-conversation-wrapper">
                                                <ul className="chat-item-list">
                                                    {/* Chat Messages */}
                                                    {chatMessages.map((message) => (
                                                        <li
                                                            className={`chat-item ${message.type === "user" ? "odd" : "even"}`}
                                                            key={message.timestamp}
                                                        >
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    {message.type === "user" ? (
                                                                        <>
                                                                            <div className="chatuser-img"><RiUser3Line /></div>
                                                                            <h3 className="chatMsgName">{user_nickname}</h3>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div
                                                                                className="chatbot-img"
                                                                                style={{ backgroundImage: `url(${companyLogo})` }}
                                                                            ></div>
                                                                            <h3>{message.name}</h3>
                                                                        </>
                                                                    )}
                                                                </div>

                                                                {/* <div className="chat-item-body">
                                                                    <p id={message.appendID} dangerouslySetInnerHTML={{ __html:convertTextPattern(convertN2lbr(message.text))}} />
                                                                    <small className='data-time-txt'>{formatRelativeDate(message.timestamp)}</small>
                                                                </div> */}
                                                                <div className="chat-item-body">
                                                                    {(() => {
                                                                        const result = maskReflectionPrompt(message.text);
                                                                        if (result.masked) {
                                                                            return (result.question);
                                                                        }
                                                                        return (
                                                                        <p
                                                                            id={message.appendID}
                                                                            dangerouslySetInnerHTML={{
                                                                            __html: convertTextPattern(convertN2lbr(message.text))
                                                                            }}
                                                                        />
                                                                        );
                                                                    })()}
                                                                    <small className="data-time-txt">{formatRelativeDate(message.timestamp)}</small>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}

                                                    {/* Thinking indicator (only before stream starts) */}
                                                    {isThinking && !currentStreamingMessage && (
                                                        <li className="chat-item even">
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    <div
                                                                        className="chatbot-img"
                                                                        style={{ backgroundImage: `url(${companyLogo})` }}
                                                                    ></div>
                                                                    <h3>ARI</h3>
                                                                </div>
                                                                <div className="chat-item-body">
                                                                    <div className="thinking-loader"><div className="thinking-dot"></div><div className="thinking-dot"></div><div className="thinking-dot"></div></div>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}

                                                    {/* Streaming bubble (ONLY while thinking) */}
                                                    {isThinking && currentStreamingMessage && (
                                                        <li className="chat-item even">
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    <div
                                                                        className="chatbot-img"
                                                                        style={{ backgroundImage: `url(${companyLogo})` }}
                                                                    ></div>
                                                                    <h3>ARI</h3>
                                                                </div>
                                                                <div className="chat-item-body">
                                                                    <p dangerouslySetInnerHTML={{ __html:convertTextPattern(convertN2lbr(currentStreamingMessage))}} />
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}

                                                    <div ref={messagesEndRef} />
                                                </ul>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="chat-welcome-container">
                                            <h3 className="chat-icon-center">
                                                <BsChatDots />
                                            </h3>
                                            <h2>{categoryDetails.category_name}</h2>
                                            <div className="chat-box-suggestions-wrapper">
                                                <h5 className='text-center' dangerouslySetInnerHTML={{ __html:convertTextPattern(convertN2lbr(categoryDetails.category_description ?? ''))}} />
                                            </div>
                                        </div>
                                    )
                                )
                            }
                        </div>
                        
                        <div className="chat-box-bottom-container">
                            <ChatBox
                                isBotTyping={isBotTyping}
                                onChange={onChange}
                                inputMessage={inputMessage}
                                inputHeight={inputHeight}
                                handleKeyDown={handleKeyDown}
                                formRef={formRef}
                                onSubmit={onSubmit}
                                categoryID={categoryID}
                                textareaRef={textareaRef}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="main-chat-container">
                        <div className="chat-box-top-container">
                            <a href="/" className='chat-heading-container'>
                                <span>Welcome to ARI: Upgrade your plan.</span>
                            </a>
                        </div>
                        <div className="chat-box-center-container">
                            <UpgradeRequired />
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default UserCategoryThreads;